import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  GetIntervalCumulativeAmounts,
  type JournalReportDefaultIntervalItem,
} from 'donor-elf-web/lib/journal-utils';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import JournalStageModel from 'donor-elf-web/models/journal-stage';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import { storageFor } from 'ember-local-storage';
import Highcharts from 'highcharts';
import moment from 'moment';

export default class JournalReportDefaultController extends Controller {
  @service declare formatAmountService: FormatAmountService;
  @service declare router: Router;
  @storageFor('local-settings') localSettings: any;

  @tracked showLastIntervalCount: number = 0;
  @tracked hiddenStageNames: any = {};

  get askStageName() {
    return (
      this.model.journalInfo.sortedStages.find((x) => x.askStage)?.name || 'Ask'
    );
  }

  get sortedStages() {
    return this.model.journalInfo.sortedStages;
  }

  get chartHeight() {
    return 200;
  }

  get showLastOptions() {
    let { totalIntervalCount } = this;
    let options = [
      {
        label: 'All',
        val: 0,
      },
    ];

    for (let index = 1; index <= totalIntervalCount; index++) {
      let label = index === 1 ? 'Last' : `Last ${index}`;
      options.push({
        label,
        val: index,
      });
    }

    return options;
  }

  get showIntervalTimespanOptions() {
    return [
      {
        label: 'Weeks',
        val: 'week',
      },
      {
        label: 'Days',
        val: 'day',
      },
    ];
  }

  get totalIntervalCount() {
    return this.model.formattedReport.intervalItems.length;
  }

  get selectedIntervalItems(): JournalReportDefaultIntervalItem[] {
    if (this.showLastIntervalCount === 0) {
      return this.model.formattedReport.intervalItems;
    } else {
      return this.model.formattedReport.intervalItems.slice(
        this.model.formattedReport.intervalItems.length -
          this.showLastIntervalCount,
      );
    }
  }

  get selectedIntervalStageTasks() {
    return this.selectedIntervalItems.map((item) => {
      return {
        dateRangeFormatted: item.dateRangeFormatted,
        stageInfo: this.sortedStages.map((stage) => {
          return {
            created: item.createdStageTasks[stage.id] || 0,
            attempted: item.completedStageWork[stage.id]?.attempted || 0,
            completed: item.completedStageWork[stage.id]?.completed || 0,
          };
        }),
      };
    });
  }

  get timeframe() {
    let { selectedIntervalItems } = this;
    return {
      start: selectedIntervalItems[0].date,
      end: selectedIntervalItems[selectedIntervalItems.length - 1].dateEnd,
    };
  }

  get completedTaskStagesToShow() {
    let { hiddenStageNames } = this;
    return this.model.journalInfo.sortedStages.filter(
      (x: JournalStageModel) => hiddenStageNames[x.name] !== true,
    );
  }

  // [{ goal: JournalGoal, intervalCumulativeAmounts: [] }]
  get goalsAndDecisions() {
    let {
      model: {
        formattedReport: { intervalDates, yesDecisions },
      },
    } = this;
    return this.model.journalInfo.goals.map((goal: JournalGoalModel) => {
      return {
        goal,
        intervalCumulativeAmounts: GetIntervalCumulativeAmounts(
          intervalDates,
          yesDecisions[goal.id],
        ),
      };
    });
  }

  get showProjectedGoal() {
    return this.model.intervalTimespan === 'week';
  }

  setHiddenStageNames() {
    this.hiddenStageNames = {};
    (this.localSettings.get('journalDefaultReportHideStageNames') || '')
      .split(',')
      .forEach((name: string) => (this.hiddenStageNames[name] = true));
  }

  @action
  templateDidInsert() {
    this.showLastIntervalCount = Math.min(
      this.localSettings.get('journalShowLastIntervalCount'),
      this.totalIntervalCount,
    );
    this.setHiddenStageNames();
  }

  @action
  templateWillDestroy() {
    this.showLastIntervalCount = 0;
  }

  @action
  handleHiddenStageNameChange(stage: JournalStageModel, e: any) {
    if (e.target.checked) {
      this.hiddenStageNames[stage.name] = true;
    } else {
      delete this.hiddenStageNames[stage.name];
    }
    // notify a tracked property was changed
    this.hiddenStageNames = { ...this.hiddenStageNames };
    this.localSettings.set(
      'journalDefaultReportHideStageNames',
      Object.keys(this.hiddenStageNames).join(','),
    );
  }

  @action
  handleShowLastIntervalCountChanged(val: number) {
    if (this.showLastIntervalCount !== val) {
      this.showLastIntervalCount = val;
      this.localSettings.set(
        'journalShowLastIntervalCount',
        this.showLastIntervalCount,
      );
    }
  }

  @action
  handleShowIntervalTimespanChanged(val: number) {
    this.router.transitionTo('funds.fund.journals.journal.report', {
      queryParams: { interval: val },
    });
  }

  @action
  renderNewPledgesChart(journalGoal: JournalGoalModel, e: any) {
    Highcharts.chart(e, this.getNewPledgesChart(journalGoal));
  }

  getNewPledgesChart(journalGoal: JournalGoalModel) {
    let { selectedIntervalItems, formatAmountService } = this;
    let series = [];

    series.push({
      type: 'column',
      name: 'New Pledges',
      color: '#0b68a1',
      pointPadding: 0.0,
      tooltip: {
        headerFormat: '',
        pointFormatter: function (): any {
          let newPledges = formatAmountService.format(this.y, {
            removeNoCents: true,
          });
          return `<div>${this.dateRangeFormatted}</div><div class='tw-text-sm'>Yes Decisions: ${newPledges}</div>`;
        },
      },
      data: selectedIntervalItems.map((item: any) => {
        return {
          y: item.decisions[journalGoal.id].yesAmount,
          dateRangeFormatted: item.dateRangeFormatted,
        };
      }),
    });
    if (journalGoal.weeklyGoal) {
      series.push({
        type: 'line',
        name: 'Goal',
        color: '#f0ad4e',
        lineWidth: 2,
        marker: { enabled: false },
        data: [
          { x: -0.5, y: journalGoal.weeklyGoal },
          ...selectedIntervalItems.map(() => journalGoal.weeklyGoal),
          { x: selectedIntervalItems.length - 0.5, y: journalGoal.weeklyGoal },
        ],
      });
    }

    return {
      chart: {
        height: this.chartHeight,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: selectedIntervalItems.map((x: any) => x.date),
        labels: {
          formatter: function (): any {
            return moment(this.value).format('M/D');
          },
        },
        min: 0,
        max: selectedIntervalItems.length - 1,
      },
      yAxis: {
        min: 0,
        title: {
          text: null,
        },
      },
      tooltip: {
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }
}
